/**
 * Flatten API success/error payloads into a displayable string.
 * Handles Laravel-style nested validation shapes, e.g.:
 * { payment_id: ["The payment_id has already been taken."] }
 */
export function extractApiMessage(message, fallback = "") {
	if (message == null || message === "") return fallback;
	if (typeof message === "string") return message;
	if (typeof message === "number" || typeof message === "boolean") return String(message);
	if (Array.isArray(message)) {
		const parts = message.map((m) => extractApiMessage(m)).filter(Boolean);
		return parts.join("\n") || fallback;
	}
	if (typeof message === "object") {
		const parts = [];
		Object.values(message).forEach((v) => {
			const s = extractApiMessage(v);
			if (s) parts.push(s);
		});
		return parts.join("\n") || fallback;
	}
	return fallback;
}

/**
 * Resolve a human-readable message from axios error / API body.
 */
export function getApiErrorMessage(errorOrData, fallback = "Something went wrong") {
	const data = errorOrData?.response?.data ?? errorOrData?.data ?? errorOrData;
	if (!data) return fallback;
	if (typeof data === "string") return data.trim() || fallback;
	return (
		extractApiMessage(data.message) ||
		extractApiMessage(data.errors) ||
		extractApiMessage(data.error) ||
		fallback
	);
}

/**
 * Copy Laravel-style validation bags onto a component `errors` object.
 * Supports both `errors` and nested `message` objects.
 */
export function applyApiFieldErrors(errorsTarget, errorOrData) {
	if (!errorsTarget || typeof errorsTarget !== "object") return;
	const data = errorOrData?.response?.data ?? errorOrData?.data ?? errorOrData;
	if (!data || typeof data !== "object") return;

	const bags = [];
	if (data.errors && typeof data.errors === "object" && !Array.isArray(data.errors)) {
		bags.push(data.errors);
	}
	if (data.message && typeof data.message === "object" && !Array.isArray(data.message)) {
		bags.push(data.message);
	}

	bags.forEach((bag) => {
		Object.keys(bag).forEach((k) => {
			const v = bag[k];
			errorsTarget[k] = Array.isArray(v) ? v[0] : v;
		});
	});
}

function isSwalOptionsObject(value) {
	if (!value || typeof value !== "object" || Array.isArray(value)) return false;
	return (
		"icon" in value ||
		"title" in value ||
		"text" in value ||
		"html" in value ||
		"showConfirmButton" in value ||
		"timer" in value ||
		"toast" in value ||
		"position" in value
	);
}

function coerceDisplayValue(value) {
	if (value == null) return value;
	if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
		return value;
	}
	return extractApiMessage(value) || "";
}

function isGenericSwalTitle(title) {
	const t = String(title ?? "")
		.trim()
		.toLowerCase();
	return !t || ["error", "success", "warning", "info", "validation", "failed"].includes(t);
}

/**
 * Wrap VueSweetalert2 so nested API messages never render as a blank/"Error"-only toast.
 * Toast UI mainly shows `title`, so the real message is promoted when the title is generic.
 */
export function wrapSwal(rawSwal) {
	const wrapped = function (...args) {
		if (args.length === 1 && isSwalOptionsObject(args[0])) {
			const opts = { ...args[0] };
			if ("title" in opts) opts.title = coerceDisplayValue(opts.title);
			if ("text" in opts) opts.text = coerceDisplayValue(opts.text);
			if ("html" in opts && typeof opts.html !== "string") opts.html = coerceDisplayValue(opts.html);

			const title = opts.title;
			const text = opts.text;
			if (text && isGenericSwalTitle(title)) {
				opts.title = text;
				delete opts.text;
			}
			if (typeof opts.title === "string" && opts.title.length > 40 && !opts.timer) {
				opts.timer = 4500;
			}
			return rawSwal(opts);
		}

		if (args.length >= 3) {
			const title = coerceDisplayValue(args[0]);
			const text = coerceDisplayValue(args[1]);
			const icon = args[2];
			const opts = { icon };

			if (text && isGenericSwalTitle(title)) {
				opts.title = text;
			} else {
				if (title) opts.title = title;
				if (text) opts.text = text;
			}
			if (typeof opts.title === "string" && opts.title.length > 40) {
				opts.timer = 4500;
			}
			return rawSwal(opts);
		}

		if (args.length === 2) {
			const a0 = coerceDisplayValue(args[0]);
			const a1 = coerceDisplayValue(args[1]);
			// ("Error", message) or ("", message)
			if (a1 && isGenericSwalTitle(a0)) {
				return rawSwal({
					title: a1,
					icon: "error",
					timer: String(a1).length > 40 ? 4500 : undefined,
				});
			}
			return rawSwal(a0, a1);
		}

		if (args.length === 1) {
			return rawSwal(coerceDisplayValue(args[0]));
		}

		return rawSwal(...args.map((a, i) => (i < 2 ? coerceDisplayValue(a) : a)));
	};

	Object.keys(rawSwal || {}).forEach((key) => {
		if (typeof rawSwal[key] === "function") {
			wrapped[key] = rawSwal[key].bind(rawSwal);
		} else {
			wrapped[key] = rawSwal[key];
		}
	});

	return wrapped;
}
